import Cookies from "js-cookie";
import type { UserTokens } from "./auth-user.ts";

type AuthCookieStorageConfig = {
	readonly prefix: string;
	readonly appClientId: string;
	readonly cookieDomain: string | undefined;
	readonly cookiesSecure: boolean;
	readonly expiresInDays: number;
};

function createAuthCookieStorage({
	prefix,
	appClientId,
	cookieDomain,
	cookiesSecure,
	expiresInDays,
}: AuthCookieStorageConfig) {
	const cookieAttributes = {
		secure: cookiesSecure,
		path: "/",
		domain: cookieDomain,
		// Days
		expires: expiresInDays,
	};
	const cookieNamePrefix = `${prefix}.${appClientId}.`;
	const accessCookieName = `${cookieNamePrefix}access`;
	const idCookieName = `${cookieNamePrefix}id`;
	const refreshCookieName = `${cookieNamePrefix}refresh`;

	return {
		get(): UserTokens | undefined {
			const accessValue = Cookies.get(accessCookieName);
			const idValue = Cookies.get(idCookieName);
			const refreshValue = Cookies.get(refreshCookieName);
			if (
				accessValue === undefined ||
				idValue === undefined ||
				refreshValue === undefined
			) {
				return undefined;
			}

			return {
				accessToken: { accessToken: accessValue },
				idToken: { idToken: idValue },
				refreshToken: { refreshToken: refreshValue },
			};
		},
		set({
			accessToken: { accessToken },
			idToken: { idToken },
			refreshToken: { refreshToken },
		}: UserTokens) {
			Cookies.set(accessCookieName, accessToken, cookieAttributes);
			Cookies.set(idCookieName, idToken, cookieAttributes);
			Cookies.set(refreshCookieName, refreshToken, cookieAttributes);
		},
		clear() {
			Cookies.remove(accessCookieName, cookieAttributes);
			Cookies.remove(idCookieName, cookieAttributes);
			Cookies.remove(refreshCookieName, cookieAttributes);
		},
	};
}

type AuthCookieStorage = ReturnType<typeof createAuthCookieStorage>;

export type { AuthCookieStorage, AuthCookieStorageConfig };
export { createAuthCookieStorage };
